import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { fallanalyseAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  TreeDeciduous, Mail, Lock, User, ArrowRight, Truck,
  ShieldCheck, Compass, ShieldAlert, CheckCircle2, Bookmark, LogIn,
  CreditCard, Eye, EyeOff,
} from 'lucide-react';
import { trackRegistrationStart } from '@/lib/tracking';

const RegisterPage = () => {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [entwurf, setEntwurf] = useState(null);
  const entwurfId = searchParams.get('entwurf');
  const plan = searchParams.get('plan');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: searchParams.get('role') || (plan === 'premium' ? 'waldbesitzer' : ''),
  });
  const [showPassword, setShowPassword] = useState(false);

  // Premium-Plan: Rolle vorbelegen und direkt zum Formular
  useEffect(() => {
    if (plan === 'premium') {
      setFormData(prev => ({ ...prev, role: 'waldbesitzer' }));
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  // Entwurf laden wenn vorhanden
  useEffect(() => {
    if (entwurfId) {
      fallanalyseAPI.projektEntwurfAbrufen(entwurfId)
        .then(res => {
          setEntwurf(res.data);
          if (!formData.role) {
            setFormData(prev => ({ ...prev, role: 'waldbesitzer' }));
            setStep(2);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entwurfId]);

  useEffect(() => {
    if (formData.role && step === 1) {
      trackRegistrationStart(formData.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password || !formData.role) {
      toast.error(t('common.fillAllFields') || 'Bitte füllen Sie alle Felder aus');
      return;
    }

    setLoading(true);
    try {
      const result = await register(formData.email, formData.password, formData.name, formData.role, 'de');
      toast.success(t('toasts.registrierungErfolg'));

      // Entwurf dem neuen User zuordnen
      if (entwurfId && result?.user?.id) {
        try {
          await fallanalyseAPI.projektEntwurfZuordnen(entwurfId, result.user.id);
          toast.success(t('toasts.analyseGespeichert'));
        } catch {
          // Nicht kritisch
        }
      }

      // Premium-Plan: Zum Schnell-Check leiten, damit der Nutzer seinen ersten Fall erstellen kann
      if (plan === 'premium') {
        navigate('/schnell-check');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.registrierungFehler'));
    } finally {
      setLoading(false);
    }
  };

  // Login-/Register-Auswahl – immer sichtbar wenn entwurf vorhanden
  const loginHint = (
    <div className="mt-4 text-center text-sm text-gray-500" data-testid="login-hint">
      {t('auth.alreadyRegistered')}{' '}
      <Link
        to={entwurfId ? `/anmelden?entwurf=${entwurfId}` : '/anmelden'}
        className="text-primary hover:underline font-medium"
        data-testid="login-link"
      >
        {t('auth.loginNow')}
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-warm-50 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <div className="p-2 rounded-lg bg-primary">
            <TreeDeciduous className="h-6 w-6 text-white" />
          </div>
          <span className="font-serif text-2xl font-bold text-primary">ForestHub</span>
        </Link>

        {/* Premium-Plan Banner */}
        {plan === 'premium' && !entwurf && (
          <Card className="mb-4 border-primary/20 bg-primary/[0.03]" data-testid="premium-plan-banner">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground mb-1">{t('auth.premiumBannerTitle')}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t('auth.premiumBannerDesc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Entwurf-Vorschau */}
        {entwurf && (
          <Card className="mb-4 border-primary/15 bg-primary/[0.02]" data-testid="entwurf-vorschau">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <Bookmark className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground mb-1">Ihre Analyse wird gespeichert</p>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-2">{entwurf.zusammenfassung}</p>
                  <div className="space-y-1">
                    {entwurf.vorentscheidung?.sinnvollster_weg && (
                      <div className="flex items-start gap-1.5">
                        <Compass className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                        <span className="text-xs text-gray-600 truncate">{entwurf.vorentscheidung.sinnvollster_weg}</span>
                      </div>
                    )}
                    {entwurf.vorentscheidung?.groesstes_risiko?.text && (
                      <div className="flex items-start gap-1.5">
                        <ShieldAlert className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                        <span className="text-xs text-gray-600 truncate">{entwurf.vorentscheidung.groesstes_risiko.text}</span>
                      </div>
                    )}
                    {entwurf.aufgaben?.length > 0 && (
                      <div className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                        <span className="text-xs text-gray-600">{entwurf.aufgaben.length} offene Punkte zum Ergänzen</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Wenn entwurf vorhanden: Prominente Wahl "Registrieren" ODER "Anmelden" */}
        {entwurf && step === 2 && (
          <div className="grid grid-cols-2 gap-3 mb-4" data-testid="auth-choice">
            <Link to={entwurfId ? `/anmelden?entwurf=${entwurfId}` : '/anmelden'}>
              <Button variant="outline" className="w-full h-11 text-sm" data-testid="auth-choice-login">
                <LogIn className="h-4 w-4 mr-2" />
                Anmelden
              </Button>
            </Link>
            <Button className="w-full h-11 text-sm" disabled data-testid="auth-choice-register">
              <User className="h-4 w-4 mr-2" />
              Registrieren
            </Button>
          </div>
        )}

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {entwurf ? 'Analyse sichern' : t('auth.register')}
            </CardTitle>
            <p className="text-gray-500 text-sm">
              {entwurf
                ? 'Registrieren Sie sich kostenlos, um Ihren Fall zu speichern und später darauf zurückzukommen.'
                : step === 1 ? t('auth.whatToDo') : t('auth.createAccount')}
            </p>
          </CardHeader>

          <CardContent>
            {step === 1 ? (
              <div className="space-y-4">
                <button
                  onClick={() => { setFormData({ ...formData, role: 'waldbesitzer' }); setStep(2); }}
                  className={`w-full p-5 rounded-xl border-2 text-left transition-all hover:border-primary/50 ${
                    formData.role === 'waldbesitzer' ? 'border-primary bg-primary/5' : 'border-warm-200'
                  }`}
                  data-testid="role-waldbesitzer"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-xl bg-primary/10">
                      <TreeDeciduous className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t('landing.startProject')}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t('auth.ownerOption')}</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => { setFormData({ ...formData, role: 'anbieter' }); setStep(2); }}
                  className={`w-full p-5 rounded-xl border-2 text-left transition-all hover:border-accent/50 ${
                    formData.role === 'anbieter' ? 'border-accent bg-accent/5' : 'border-warm-200'
                  }`}
                  data-testid="role-anbieter"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-xl bg-accent/10">
                      <Truck className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t('landing.offerServices')}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t('auth.providerOption')}</p>
                    </div>
                  </div>
                </button>

                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-200 mt-4">
                  <ShieldCheck className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700">{t('auth.noSpam')}</p>
                </div>

                {loginHint}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Role Badge */}
                <div className="flex justify-center mb-3">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    formData.role === 'waldbesitzer' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
                  }`}>
                    {formData.role === 'waldbesitzer' ? (
                      <><TreeDeciduous className="h-4 w-4" /> {t('landing.forOwners')}</>
                    ) : (
                      <><Truck className="h-4 w-4" /> {t('landing.forProviders')}</>
                    )}
                    {!entwurf && (
                      <button type="button" onClick={() => setStep(1)} className="ml-2 text-xs underline">
                        {t('auth.change')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Max Mustermann" className="pl-10" data-testid="register-name-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>E-Mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="ihre@email.de" className="pl-10" data-testid="register-email-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Passwort</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder={t('resetPw.mindestens8')} className="pl-10 pr-10" data-testid="register-password-input" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="register-toggle-password"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <ShieldCheck className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700">{t('auth.noSpam')}</p>
                </div>

                <Button
                  type="submit" disabled={loading}
                  className={`w-full ${formData.role === 'waldbesitzer' ? 'bg-primary hover:bg-primary-600' : 'bg-accent hover:bg-accent/90'}`}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Wird registriert...' : entwurf ? 'Registrieren und Analyse sichern' : t('auth.createAccountBtn')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <p className="text-xs text-center text-gray-500">
                  {t('auth.termsAgreement')}{' '}
                  <Link to="/agb" className="underline">{t('auth.termsLink')}</Link> {t('auth.and')}{' '}
                  <Link to="/datenschutz" className="underline">{t('auth.privacyLink')}</Link>{t('auth.agree')}
                </p>

                {/* Login hint auch unter dem Formular */}
                {!entwurf && loginHint}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
